import { AFTER_SAVE } from "@/components/molecules/form-footer";
import { Field } from "@/components/molecules/field";
import { SettingsSection } from "@/components/molecules/settings-section";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  useClientDeletionEligibility,
  useDeleteClient,
  useMyClients,
} from "@/features/clients/hooks/use-clients";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import type { ClientDeletionBlocker } from "@/features/clients/types";
import { useTranslation } from "@/features/utils/i18n";
import { TrashIcon, WarningIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

/**
 * Permanently delete the company.
 *
 * Deletion is irreversible and wipes every table scoped to the tenant, so the button
 * is only live on a company that is demonstrably unused: the owner is the last member
 * left, no invitation is outstanding, and nothing external is still wired to it. The
 * backend enforces all of that — what this section adds is telling the owner *which*
 * condition is not met, since "delete is disabled" on its own is a dead end.
 */
export function DeleteCompanySection() {
  const { t } = useTranslation("companySettings");
  const { company: clientId } = useParams<{ company: string }>();
  const navigate = useNavigate();
  const isOwner = useHasRole("OWNER");

  const { data: clients = [] } = useMyClients();
  const currentClient = useMemo(
    () => clients.find((c) => c.client.id === clientId),
    [clients, clientId],
  );

  const { data: eligibility, isLoading } = useClientDeletionEligibility(
    clientId || "",
    isOwner,
  );
  const deleteClient = useDeleteClient();

  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");

  if (!isOwner) return null;

  const companyName = currentClient?.client.name || "";
  // Owners already know they are owners; showing them "you are not an owner" would be
  // noise, and the section is hidden from everyone else anyway.
  const blockers = (eligibility?.blockers || []).filter(
    (b) => b !== "not_owner",
  );
  const canDelete = !!eligibility?.can_delete;

  const blockerText = (blocker: ClientDeletionBlocker) =>
    t(`companySettings.delete.blockers.${blocker}`, {
      count: eligibility?.counts[blocker as keyof typeof eligibility.counts],
    });

  const handleDelete = async () => {
    if (!clientId) return;
    try {
      await deleteClient.mutateAsync(clientId);
      toast.success(t("companySettings.delete.deleted"));
      setOpen(false);
      // The company is gone, and the settings form's unsaved edits with it.
      navigate("/", AFTER_SAVE);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("companySettings.delete.failed"),
      );
    }
  };

  return (
    <SettingsSection
      tone="danger"
      title={
        <span className="flex items-center gap-2">
          <WarningIcon className="h-5 w-5" />
          {t("companySettings.delete.title")}
        </span>
      }
      description={t("companySettings.delete.description")}
    >
        {blockers.length > 0 && (
          <div className="rounded-md border border-muted bg-muted/40 p-3">
            <p className="text-sm font-medium">
              {t("companySettings.delete.blockedTitle")}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {blockers.map((blocker) => (
                <li key={blocker}>{blockerText(blocker)}</li>
              ))}
            </ul>
          </div>
        )}
        <div>
          <Button
            type="button"
            variant="destructive"
            disabled={isLoading || !canDelete}
            onClick={() => {
              setConfirmation("");
              setOpen(true);
            }}
          >
            <TrashIcon className="h-4 w-4" />
            {t("companySettings.delete.action")}
          </Button>
        </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("companySettings.delete.confirmTitle")}</DialogTitle>
            <DialogDescription>
              {t("companySettings.delete.confirmDescription", {
                name: companyName,
              })}
            </DialogDescription>
          </DialogHeader>
          <Field
            className="py-2"
            label={t("companySettings.delete.confirmLabel", {
              name: companyName,
            })}
            htmlFor="delete_confirmation"
          >
            <Input
              id="delete_confirmation"
              autoComplete="off"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={companyName}
            />
          </Field>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("companySettings.delete.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={
                deleteClient.isPending ||
                confirmation.trim() !== companyName.trim()
              }
              onClick={() => void handleDelete()}
            >
              {deleteClient.isPending
                ? t("companySettings.delete.deleting")
                : t("companySettings.delete.confirmAction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsSection>
  );
}
